import {
  Component, ElementRef, ViewChild, OnInit,
  DoCheck, IterableDiffers, Input, Output, EventEmitter, ContentChild
} from '@angular/core';
import { CfCoreComponent } from '../core/core.component';
import { AutocompleteModel } from '../../models/autocomplete/autocomplete.model';
import { AutocompleteStylingModel } from '../../models/autocomplete/autocomplete-styling.model';
import { AutocompleteService } from '../../services/autocomplete-service/autocomplete.service';
import { AutocompleteTemplates } from '../../templates/autocomplete.template';
import { TemplateService } from '../../services/template-service/template.service';
/**
 * <p>CF Autocomplete Component</p>
 * <pre><code>
 * import {{'{'}} AutocompleteModel {{'}'}} from 'cedrus-fusion'
 * import {{'{'}} AutocompleteStylingModel {{'}'}} from 'cedrus-fusion'
 * <code><</code>cf-autocomplete [properties]="myAutocomplete" [styling]="myAutocompleteStyles"<code>></code>
 * <code><</code><code>/</code>cf-autocomplete<code>></code>
 * </code></pre>
 */
export class CfAutocompleteComponent extends CfCoreComponent implements OnInit, DoCheck {
/**
 * <p>Example of model:</p>
 * <code><pre>{{'{'}}
 *  displayField: "label",
 *  source: this.mySource,
 *  useChips: true,
 *  chipsSelected: "multiple",
 *  chipsReadOnly: false,
 *  multiselection: false,
 *  uniqueSelected: true,
 *  stayOpenedOnClick: true,
 *  highlighting: false,
 *  prefetch: true,
 *  cache: true,
 *  delay: 1000
 * {{'}'}}</pre></code>
 * <h4>List of all model properties</h4>
 * <h4>displayField: string</h4>
 * Name of item property to be used as item label and selected value. Required.
 * <h4>source: any[]</h4>
 * Array with items. Has lower priority then 'serverUrl'.
 * <h4>serverUrl: string</h4>
 * Server url to be called for to take items. Has highter priority then 'source'.
 * <h4>useChips: boolean</h4>
 * Using chips for selected items. Default: false.
 * <h4>chipsSelected: string</h4>
 * If useChips is true, then it is possible to set chips selection: single or multiple. Default: multiple.
 * <h4>chipsReadOnly: boolean</h4>
 * If chips can't be removed after selection. Default: false.
 * <h4>multiselection: boolean</h4>
 * If dropdown item can be selected multiple times. Default: true.
 * <h4>uniqueSelected: boolean</h4>
 * Search items with using comma inside search input field. Default: false.
 * <h4>stayOpenedOnClick: boolean</h4>
 * If dropdown must be opened on selecting items. Default: false.
 * <h4>highlighting: boolean</h4>
 * If to highlight search value in items labels. Default: false.
 * <h4>cache: boolean</h4>
 * If to cache search results. Default: false.
 * <h4>prefetch: boolean</h4>
 * If to send request to a server on component init. Default: false.
 * <h4>delay: boolean</h4>
 * The amount of time in milliseconds from when the user stops typing to when the result is returned. Default: 250.
 */
properties: AutocompleteModel;
/**
 * Object for all styling properties
 * <h4>container</b>: StylingModel</h4>
 * StylingModel for styling component container
 * <h4>chipsContainer</b>: StylingModel</h4>
 * StylingModel for styling chips container
 * <h4>chips</b>: StylingModel</h4>
 * StylingModel for styling chips item
 * <h4>searchContainer</b>: StylingModel</h4>
 * StylingModel for styling search container with input and dropdown
 * <h4>inputContainer</b>: StylingModel</h4>
 * StylingModel for styling input container
 * <h4>input</b>: StylingModel</h4>
 * StylingModel for styling input element
 * <h4>dropdownContainer</b>: StylingModel</h4>
 * StylingModel for styling dropdown container
 * <h4>dropdownItem</b>: StylingModel</h4>
 * StylingModel for styling dropdown item
 */
styling: AutocompleteStylingModel;
/**
 * Name of item property to be used as item label and selected value. Required.
 * @param {?} value
 * @return {?}
 */
set displayField(value: string) { this.properties.displayField = value; }
/**
 * Array with items. Has lower priority then 'serverUrl'.
 * @param {?} value
 * @return {?}
 */
set source(value: any[]) { this.properties.source = value; }
/**
 * Server url to be called for to take items. Has highter priority then 'source'.
 * @param {?} value
 * @return {?}
 */
set serverUrl(value: string) { this.properties.serverUrl = value; }
/**
 * Using chips for selected items. Default: false.
 * @param {?} value
 * @return {?}
 */
set useChips(value: boolean) { this.properties.useChips = value; }
/**
 * If useChips is true, then it is possible to set chips selection: single or multiple. Default: multiple.
 * @param {?} value
 * @return {?}
 */
set chipsSelected(value: string) { this.properties.chipsSelected = value; }
/**
 * If chips can't be removed after selection. Default: false.
 * @param {?} value
 * @return {?}
 */
set chipsReadOnly(value: boolean) { this.properties.chipsReadOnly = value; }
/**
 * Search items with using comma inside search input field. Default: false.
 * @param {?} value
 * @return {?}
 */
set multiselection(value: boolean) { this.properties.multiselection = value; }
/**
 * If dropdown item can be selected multiple times. Default: true.
 * @param {?} value
 * @return {?}
 */
set uniqueSelected(value: boolean) { this.properties.uniqueSelected = value; }
/**
 * If dropdown must be opened on selecting items. Default: false.
 * @param {?} value
 * @return {?}
 */
set stayOpenedOnClick(value: boolean) { this.properties.stayOpenedOnClick = value; }
/**
 * If to highlight search value in items labels. Default: false.
 * @param {?} value
 * @return {?}
 */
set highlighting(value: boolean) { this.properties.highlighting = value; }
/**
 * To cache search results. Default: false.
 * @param {?} value
 * @return {?}
 */
set cache(value: boolean) { this.properties.cache = value; }
/**
 * If to send request to a server on component init. Default: false.
 * @param {?} value
 * @return {?}
 */
set prefetch(value: boolean) { this.properties.prefetch = value; }
/**
 * The amount of time in milliseconds from when the user stops typing to when the result is returned. Default: 250.
 * @param {?} value
 * @return {?}
 */
set delay(value: boolean) { this.properties.delay = value; }
/**
 * It is output event, which send search value.
 */
onSearch: EventEmitter<any> = new EventEmitter<any>();
/**
 * It is output event, which send array with selected item/items.
 */
onSelect: EventEmitter<any> = new EventEmitter<any>();
/**
 * @hidden 
 * User template for items in dropdown
 */
dropdownItemTemplate;
/**
 * @hidden 
 * User template for selected chips
 */
selectedItemTemplate;
/**
 * @hidden 
 * Array for default init items from user or server
 */
itemsToBeFiltered: any[] = [];
/**
 * @hidden 
 * Temprorary array with filtered items during filtering execution and after to be passed to filteredItems
 */
tempFiltered: any[] = [];
/**
 * @hidden 
 * Array with filtered items which is using inside dropdown in html
 */
filteredItems: any[] = [];
/**
 * @hidden 
 * Means if user focused search input field or is working with opened dropdown list items
 */
inputIsFocused = false;
/**
 * @hidden 
 * This is state of autocomplete to check from where search value was changed
 */
selectedByUser = true;
/**
 * @hidden 
 * This is autocomplete search value
 */
searchValue = '';
/**
 * @hidden 
 * It is array with selected items as chips
 */
selectedItems: any[] = [];
/**
 * @hidden 
 * It is array with selected items as multiselection by comma inside search input
 */
selection: any[] = [];
/**
 * @hidden 
 * Means selected items as labels inside search input separated by comma
 */
multipleSelected = '';
/**
 * @hidden 
 * If search value was changed by user
 */
typedByUser = false;
/**
 * @hidden 
 * Cache for search results
 */
cached: any = {};
/**
 * @hidden 
 * Timeout for user typing
 */
typingTimer: any;
/**
 * @hidden 
 * Object to register element state and compute differences.
 */
differ: any;
/**
 * @hidden 
 * Method to resolve to show dropdown items list
 * @return {?}
 */
private get _resolveList() {
    let /** @type {?} */ _resolved = false;

    if (!!this.filteredItems && !!this.properties) {
      if (this.inputIsFocused && this.filteredItems.length > 0) {
        _resolved = true;
      }
      if (this.properties.useChips && this.properties.chipsSelected === 'single' && this.selectedItems.length === 1) {
        _resolved = false;
      }
    }

    return _resolved;
  }
/**
 * @hidden 
 * Constructor to upgrade component and register click events to close dropdown if needed
 * @param {?} elementRef
 * @param {?} templateService
 * @param {?} autocompleteService
 * @param {?} differs
 */
constructor(public elementRef: ElementRef,
              /**@hidden */ templateService: TemplateService,
public autocompleteService: AutocompleteService,
    differs: IterableDiffers) {
    super(elementRef, templateService);
    document.addEventListener('click', this.checkIfToCloseDropdown.bind(this));
    this.differ = differs.find([]).create(null);
    this.properties = new AutocompleteModel();
    this.styling = new AutocompleteStylingModel();
  }
/**
 * @hidden 
 * Initializing component data
 * @return {?}
 */
ngOnInit() {
    this.getMyTemplate('autocomplete', AutocompleteTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'AUTOCOMPLETE').then( sampler => {
        let /** @type {?} */ active = new AutocompleteStylingModel(this.activeTemplate.style);
        this.styling = Object.assign(active, this.styling);
        // console.log('AUTOCOMPLETE', this);
        this.itemsToBeFiltered = this.properties.source;
        if (this.properties.prefetch && this.properties.serverUrl !== '') { this.takeData(''); }
      });
    });
  }
/**
 * @hidden 
 * Checking changes in component properties
 * @return {?}
 */
ngDoCheck() {
    if (this.properties != null) {
      let /** @type {?} */ changes = this.differ.diff(this.properties.source);
      if (changes) { this.itemsToBeFiltered = [...changes.collection]; }
    }
  }
/**
 * @hidden 
 * Function to show all items
 * @return {?}
 */
showAllItems() {
    if (!this.properties.disable) {
      if (!this.properties.multiselection) {
        this.searchValue = '';
        this.filterItems(this.searchValue);
      } else {
        if (this.selection.length === 0) {
          this.filteredItems = this.itemsToBeFiltered;
        }
      }
    }
  }
/**
 * @hidden 
 * Function to check if to close dropdown
 * @param {?} e  click event object
 * @return {?}
 */
checkIfToCloseDropdown(e: any) {
    let /** @type {?} */ elementClasses = e.target.className;
    if (
      typeof elementClasses === 'string' &&
      elementClasses.indexOf('cf-autocomplete-arrow') === -1 &&
      elementClasses.indexOf('cf-autocomplete-input') === -1 &&
      elementClasses.indexOf('cf-autocomplete-item-ripple') === -1
    ) { this.inputIsFocused = false; }
  }
/**
 * @hidden 
 * Function to start searching mechanism
 * @return {?}
 */
searchFocused() {
    if (!this.properties.disable) {
      this.selectedByUser = false;
      if (this.searchValue !== '') { this.search(this.searchValue, false); }
      this.inputIsFocused = true;
    }
  }
/**
 * @hidden 
 * Function to set filtering system parameters
 * @param {?} inputValue  string value by which items will be filtered
 * @param {?} userTyped   boolean value means if search value was changed by user typing
 * @return {?}
 */
search(inputValue: string, userTyped: boolean) {
    if (userTyped) { this.typedByUser = true; }

    if (this.typingTimer) { clearTimeout(this.typingTimer); }

    this.typingTimer = setTimeout(() => {
      if (!this.selectedByUser) {
        let /** @type {?} */ term = inputValue.toLowerCase().trim();
        this.tempFiltered = [];
        this.filteredItems = [];
        if (this.properties.multiselection) {
          this.checkDelimited(term);
        } else {
          if (this.properties.serverUrl === '') {
            this.filterItems(term);
          } else {
            if (!this.checkInCache(term)) {
              this.takeData(term);
            } else {
              this.filterItems(term);
            }
          }
        }
      }
    }, this.properties.delay);
  }
/**
 * @hidden 
 * Function run filtering system
 * @param {?} term  string value by which items will be filtered this.filteredItems
 * @return {?}
 */
filterItems(term: string) {
    this.tempFiltered = [];
    for (let /** @type {?} */ i = 0; i < this.itemsToBeFiltered.length; i++) {
      if (this.itemsToBeFiltered[i][this.properties.displayField].toLowerCase().indexOf(term) !== -1) {
        if (!this.properties.uniqueSelected) {
          this.tempFiltered.push(this.itemsToBeFiltered[i]);
        } else {
          if (this.checkIfSelected(this.itemsToBeFiltered[i])) {
            this.tempFiltered.push(this.itemsToBeFiltered[i]);
          }
        }
      }
    }
    this.filteredItems = this.tempFiltered;
    this.onSearch.emit(term);
  }
/**
 * @hidden 
 * Function for multiselection filtering
 * @param {?} term  string value by which items will be filtered
 * @return {?}
 */
checkDelimited(term: string) {
    let /** @type {?} */ ix = term.lastIndexOf(',');
    this.multipleSelected = term.substring(0, ix + 1);
    let /** @type {?} */ currentTerm = term.substring(ix + 1);
    this.filterItems(currentTerm);
  }
/**
 * @hidden 
 * Function for highlighting search value in items labels
 * @param {?} label  item label
 * @return {?}
 */
highlight(label: string) {
    let /** @type {?} */ term = this.searchValue;
    if (this.properties.multiselection) {
      let /** @type {?} */ ix = this.searchValue.lastIndexOf(',');
      term = this.searchValue.substring(ix + 1);
    }
    let /** @type {?} */ highlight_regex = new RegExp('(' + term + ')', 'gi');
    return label.replace(highlight_regex, '<span class="cf-highlight">$1</span>');
  }
/**
 * @hidden 
 * Function for return true/false if item can be selected
 * @param {?} item  item json object
 * @return {?}
 */
checkIfSelected(item: any) {
    let /** @type {?} */ canBeSelected = true;
    if (this.properties.uniqueSelected) {
      if (this.properties.useChips) {
        canBeSelected = this.selectedItems.indexOf(item) === -1 ? true : false;
      } else {
        canBeSelected = this.selection.indexOf(item) === -1 ? true : false;
      }
    }
    return canBeSelected;
  }
/**
 * @hidden 
 * Function for selecting items in dropdown list
 * @param {?} item  item json object
 * @return {?}
 */
selectItem(item: any) {
    this.selectedByUser = true;
    if (this.properties.useChips) {
      if (this.checkIfSelected(item)) {
        if (this.properties.chipsSelected === 'multiple') {
          this.selectedItems.push(item);
        } else if (this.properties.chipsSelected === 'single' && this.selectedItems.length === 0) {
          this.selectedItems.push(item);
        }
        if (!this.properties.stayOpenedOnClick) { this.inputIsFocused = false; }
        this.onSearch.emit(this.searchValue);
        this.onSelect.emit(this.selectedItems);
      }
    } else {
      if (this.checkIfSelected(item)) {
        if (this.properties.multiselection) {
          if (!this.typedByUser && this.selection.length > 0) {
            this.multipleSelected = this.searchValue + ',';
            this.searchValue = this.multipleSelected + item[this.properties.displayField];
          } else {
            this.searchValue = this.multipleSelected + item[this.properties.displayField];
          }
          this.typedByUser = false;
          this.selection.push(item);
        } else {
          this.selection = [];
          this.selection.push(item);
          this.searchValue = item[this.properties.displayField];
        }
        if (!this.properties.stayOpenedOnClick) { this.inputIsFocused = false; }
        this.onSearch.emit(this.searchValue);
        this.onSelect.emit(this.selection);
      }
    }
    this.checkDelimited(this.searchValue);
  }
/**
 * @hidden 
 * Function for deleting items from chips array
 * @param {?} item  item json object
 * @param {?} i  item index in chips array
 * @return {?}
 */
removeSelectedItem(item: any, i: number) {
    this.selectedItems.splice(i, 1);
    this.filterItems(this.searchValue);
    this.onSelect.emit(this.selectedItems);
  }
/**
 * @hidden 
 * Function to check if search value match in previous search requests
 * @param {?} value  search text to be sent as request param
 * @return {?}
 */
checkInCache(value) {
    let /** @type {?} */ keys = Object.keys(this.cached), /** @type {?} */
      match = false, /** @type {?} */
      exists = false, /** @type {?} */
      matched;

    // tslint:disable-next-line:forin
    for (let /** @type {?} */ key in keys) {
      if (keys[key].indexOf(value) !== -1) {
        match = true;
        matched = keys[key];
      }
      if (keys[key] === value) { exists = true; }
    }
    return match;
  }
/**
 * @hidden 
 * Function to compare two objects for equality
 * @param {?} obj1  object
 * @param {?} obj2  object
 * @return {?}
 */
compare(obj1, obj2) {
    // tslint:disable-next-line:forin
    for (let /** @type {?} */ p in obj1) {
      if (obj1.hasOwnProperty(p) !== obj2.hasOwnProperty(p)) { return false; }

      switch (typeof (obj1[p])) {
        case 'object':
          if (!this.compare(obj1[p], obj2[p])) { return false; }
          break;
        case 'function':
          if (typeof (obj2[p]) === 'undefined' || (p !== 'compare' && obj1[p].toString() !== obj2[p].toString())) { return false; }
          break;
        default:
          if (obj1[p] !== obj2[p]) { return false; }
      }
    }
    for (let /** @type {?} */ p in obj2) {
      if (typeof (obj1[p]) === 'undefined') { return false; }
    }
    return true;
  };
/**
 * @hidden 
 * Function to merge all cached results into main working array: itemsToBeFiltered
 * @param {?} value  name of array in cahced object
 * @return {?}
 */
mergeCached(value) {
    this.cached[value].forEach(item => {
      let /** @type {?} */ isUnique = true;
      for (let /** @type {?} */ i = 0; i < this.itemsToBeFiltered.length; ++i) {
        if (this.compare(item, this.itemsToBeFiltered[i])) { isUnique = false; }
      }
      if (isUnique) { this.itemsToBeFiltered.push(item); }
    });

    let /** @type {?} */ that = this;
    this.itemsToBeFiltered.sort(function (a, b) {
      if (a[that.properties.displayField].toLowerCase() < b[that.properties.displayField].toLowerCase()) { return -1; }
      if (a[that.properties.displayField].toLowerCase() > b[that.properties.displayField].toLowerCase()) { return 1; }
      return 0;
    });

    this.filterItems(this.searchValue);
  }
/**
 * @hidden 
 * Function to call AutocompleteService function to make request to remote server
 * @param {?} value  search text to be sent as request param
 * @return {?}
 */
takeData(value: string) {
    if (!this.properties.disable) {
      let /** @type {?} */ url = '';
      url = url + value ? this.properties.serverUrl + value : this.properties.serverUrl;
      this.autocompleteService.getData(url).subscribe(
        data => {
          if (this.properties.cache) {
            if (!this.cached.hasOwnProperty(value)) {
              this.cached[value] = data;
              this.mergeCached(value);
            }
          } else {
            this.itemsToBeFiltered = [...data];
            this.filterItems(value);
          }
        },
        err => {
        },
        () => {
        }
      );
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-autocomplete',
  template: "<div *ngIf=\"_inited && properties?.display\" class=\"{{properties?.disable ? 'cf-autocomplete-disable' : ''}} {{activeTemplate.style?.container?.class}} {{styling?.container?.class}} {{getDynamicClasses(activeTemplate.style?.container?.dynamicClass)}} {{getDynamicClasses(styling?.container?.dynamicClass)}}\" [ngClass]=\"'cf-autocomplete'\"> <div *ngFor=\"let selectedItem of selectedItems, let i = index\" class=\"mat-chip mat-selected {{activeTemplate.style?.chips?.class}} {{styling?.chips?.class}} {{getDynamicClasses(activeTemplate.style?.chips?.dynamicClass)}} {{getDynamicClasses(styling?.chips?.dynamicClass)}}\" [ngClass]=\"'cf-autocomplete-selected-item'\"> <div *ngIf=\"!selectedItemTemplate\">{{selectedItem[properties?.displayField]}}</div> <div *ngIf=\"selectedItemTemplate\" [ngTemplateOutlet]=\"selectedItemTemplate\" [ngOutletContext]=\"{index: i, item: selectedItem}\"></div> <i *ngIf=\"!properties?.chipsReadOnly\" class=\"cf-autocomplete-remove material-icons\" (click)=\"removeSelectedItem(selectedItem, i)\">close</i> </div> <div class=\"{{activeTemplate.style?.searchContainer?.class}} {{styling?.searchContainer?.class}} {{getDynamicClasses(activeTemplate.style?.searchContainer?.dynamicClass)}} {{getDynamicClasses(styling?.searchContainer?.dynamicClass)}}\" [ngClass]=\"'cf-autocomplete-search-container'\"> <md-input-container class=\"{{activeTemplate.style?.inputContainer?.class}} {{styling?.inputContainer?.class}} {{getDynamicClasses(activeTemplate.style?.inputContainer?.dynamicClass)}} {{getDynamicClasses(styling?.inputContainer?.dynamicClass)}}\" [ngClass]=\"'cf-autocomplete-input-container'\"> <input type=\"text\" mdInput disabled=\"{{properties?.disable}}\" [(ngModel)]=\"searchValue\" (ngModelChange)=\"search($event, true)\" (focus)=\"searchFocused()\" class=\"{{activeTemplate.style?.input?.class}} {{styling?.input?.class}} {{getDynamicClasses(activeTemplate.style?.input?.dynamicClass)}} {{getDynamicClasses(styling?.input?.dynamicClass)}}\" [ngClass]=\"'cf-autocomplete-input'\"> <div class=\"cf-autocomplete-arrow mat-input-placeholder\" (click)=\"showAllItems()\"></div> </md-input-container> <div class=\"cf-autocomplete-results-base\"> <div *ngIf=\"_resolveList\" class=\" {{activeTemplate.style?.dropdownContainer?.class}} {{styling?.dropdownContainer?.class}} {{getDynamicClasses(activeTemplate.style?.dropdownContainer?.dynamicClass)}} {{getDynamicClasses(styling?.dropdownContainer?.dynamicClass)}}\" [ngClass]=\"'cf-autocomplete-result-items'\"> <div *ngFor=\"let item of filteredItems, let i = index\" (click)=\"selectItem(item)\" class=\"{{activeTemplate.style?.dropdownItem?.class}} {{styling?.dropdownItem?.class}} {{getDynamicClasses(activeTemplate.style?.dropdownItem?.dynamicClass)}} {{getDynamicClasses(styling?.dropdownItem?.dynamicClass)}}\" [ngClass]=\"'cf-autocomplete-result-item'\"> <div *ngIf=\"!dropdownItemTemplate && !properties?.highlighting\">{{item[properties?.displayField]}}</div> <div *ngIf=\"!dropdownItemTemplate && properties?.highlighting\" [innerHtml]=\"highlight(item[properties?.displayField])\"></div> <div *ngIf=\"dropdownItemTemplate\" [ngTemplateOutlet]=\"dropdownItemTemplate\" [ngOutletContext]=\"{index: i, item: item}\"></div> <div class=\"cf-autocomplete-item-ripple mat-option-ripple mat-ripple\" md-ripple></div> </div> </div> </div> </div> </div> ",
  styles: [".cf-autocomplete { display: flex; flex-direction: row; flex-wrap: wrap; padding: 8px; } .cf-autocomplete-arrow { position: absolute; top: 0px; right: 0px; bottom: 0px; left: auto !important; display: flex; align-items: center; justify-content: center; width: 18px; cursor: pointer; transition: none !important; pointer-events: initial; } .cf-autocomplete-arrow:before { content: ''; position: absolute; color: currentColor; display: block; width: 0; height: 0; border-left: 5px solid transparent; border-right: 5px solid transparent; border-top: 5px solid; } .cf-autocomplete-result-item { position: relative; z-index: 0; display: block; line-height: 48px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-height: 48px; padding: 0 16px; font-size: 16px; font-family: Roboto, Helvetica Neue, sans-serif; text-align: left; text-decoration: none; cursor: pointer; outline: none; } .cf-autocomplete-result-item .cf-autocomplete-item-ripple { pointer-events: initial !important; } .cf-autocomplete-selected-item { display: inline-flex; align-items: center; font-size: 13px; line-height: 16px; padding: 8px 12px 8px 12px; border-radius: 24px; margin: 1px; } .cf-autocomplete-remove { font-size: 20px; width: 20px; height: 20px; margin-left: 4px; cursor: pointer; } .cf-autocomplete-search-container { display: inline-block; padding: 0 8px; } .cf-autocomplete-search-container.cf-contacts-template { display: flex; flex-direction: column; flex-grow: 1; min-width: 328px; } .mat-input-container.cf-contacts-template { width: 100%; } .cf-autocomplete-search-container /deep/ .mat-input-wrapper { margin: 0px !important; padding: 0px !important; } .cf-autocomplete-search-container /deep/ .mat-input-underline { bottom: 1px !important; } .cf-autocomplete-results-base { position: relative; z-index: 100; } .cf-autocomplete-result-items { position: absolute; z-index: 100; left: 0px; top: 0px; box-shadow: 0 5px 5px -3px rgba(0, 0, 0, 0.2), 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 3px 14px 2px rgba(0, 0, 0, 0.12); min-width: 200px; overflow: auto; max-width: 328px; max-height: 256px; background: #ffffff; } :host /deep/ .mat-input-infix { padding-right: 20px; } :host /deep/ .cdk-overlay-pane { width: auto !important; } :host /deep/ .mat-option.cf-autocomplete-item { min-width: 193px; min-height: 48px; height: auto; } :host /deep/ .cf-highlight { font-weight: 700; } :host /deep/ .cf-autocomplete-disable .cf-autocomplete-arrow { color: #bdbdbd; cursor: default; } "]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
{type: AutocompleteService, },
{type: IterableDiffers, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'displayField': [{ type: Input, args: ['displayField', ] },],
'source': [{ type: Input, args: ['source', ] },],
'serverUrl': [{ type: Input, args: ['serverUrl', ] },],
'useChips': [{ type: Input, args: ['useChips', ] },],
'chipsSelected': [{ type: Input, args: ['chipsSelected', ] },],
'chipsReadOnly': [{ type: Input, args: ['chipsReadOnly', ] },],
'multiselection': [{ type: Input, args: ['multiselection', ] },],
'uniqueSelected': [{ type: Input, args: ['uniqueSelected', ] },],
'stayOpenedOnClick': [{ type: Input, args: ['stayOpenedOnClick', ] },],
'highlighting': [{ type: Input, args: ['highlighting', ] },],
'cache': [{ type: Input, args: ['cache', ] },],
'prefetch': [{ type: Input, args: ['prefetch', ] },],
'delay': [{ type: Input, args: ['delay', ] },],
'onSearch': [{ type: Output },],
'onSelect': [{ type: Output },],
'dropdownItemTemplate': [{ type: ContentChild, args: ['dropdownItemTemplate', ] },],
'selectedItemTemplate': [{ type: ContentChild, args: ['selectedItemTemplate', ] },],
};
}

function CfAutocompleteComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfAutocompleteComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfAutocompleteComponent.ctorParameters;
/** @type {?} */
CfAutocompleteComponent.propDecorators;
/**
 * <p>Example of model:</p>
 * <code><pre>{{'{'}}
 *  displayField: "label",
 *  source: this.mySource,
 *  useChips: true,
 *  chipsSelected: "multiple",
 *  chipsReadOnly: false,
 *  multiselection: false,
 *  uniqueSelected: true,
 *  stayOpenedOnClick: true,
 *  highlighting: false,
 *  prefetch: true,
 *  cache: true,
 *  delay: 1000
 * {{'}'}}</pre></code>
 * <h4>List of all model properties</h4>
 * <h4>displayField: string</h4>
 * Name of item property to be used as item label and selected value. Required.
 * <h4>source: any[]</h4>
 * Array with items. Has lower priority then 'serverUrl'.
 * <h4>serverUrl: string</h4>
 * Server url to be called for to take items. Has highter priority then 'source'.
 * <h4>useChips: boolean</h4>
 * Using chips for selected items. Default: false.
 * <h4>chipsSelected: string</h4>
 * If useChips is true, then it is possible to set chips selection: single or multiple. Default: multiple.
 * <h4>chipsReadOnly: boolean</h4>
 * If chips can't be removed after selection. Default: false.
 * <h4>multiselection: boolean</h4>
 * If dropdown item can be selected multiple times. Default: true.
 * <h4>uniqueSelected: boolean</h4>
 * Search items with using comma inside search input field. Default: false.
 * <h4>stayOpenedOnClick: boolean</h4>
 * If dropdown must be opened on selecting items. Default: false.
 * <h4>highlighting: boolean</h4>
 * If to highlight search value in items labels. Default: false.
 * <h4>cache: boolean</h4>
 * If to cache search results. Default: false.
 * <h4>prefetch: boolean</h4>
 * If to send request to a server on component init. Default: false.
 * <h4>delay: boolean</h4>
 * The amount of time in milliseconds from when the user stops typing to when the result is returned. Default: 250.
 * @type {?}
 */
CfAutocompleteComponent.prototype.properties;
/**
 * Object for all styling properties
 * <h4>container</b>: StylingModel</h4>
 * StylingModel for styling component container
 * <h4>chipsContainer</b>: StylingModel</h4>
 * StylingModel for styling chips container
 * <h4>chips</b>: StylingModel</h4>
 * StylingModel for styling chips item
 * <h4>searchContainer</b>: StylingModel</h4>
 * StylingModel for styling search container with input and dropdown
 * <h4>inputContainer</b>: StylingModel</h4>
 * StylingModel for styling input container
 * <h4>input</b>: StylingModel</h4>
 * StylingModel for styling input element
 * <h4>dropdownContainer</b>: StylingModel</h4>
 * StylingModel for styling dropdown container
 * <h4>dropdownItem</b>: StylingModel</h4>
 * StylingModel for styling dropdown item
 * @type {?}
 */
CfAutocompleteComponent.prototype.styling;
/**
 * It is output event, which send search value.
 * @type {?}
 */
CfAutocompleteComponent.prototype.onSearch;
/**
 * It is output event, which send array with selected item/items.
 * @type {?}
 */
CfAutocompleteComponent.prototype.onSelect;
/**
 * @hidden 
 * User template for items in dropdown
 * @type {?}
 */
CfAutocompleteComponent.prototype.dropdownItemTemplate;
/**
 * @hidden 
 * User template for selected chips
 * @type {?}
 */
CfAutocompleteComponent.prototype.selectedItemTemplate;
/**
 * @hidden 
 * Array for default init items from user or server
 * @type {?}
 */
CfAutocompleteComponent.prototype.itemsToBeFiltered;
/**
 * @hidden 
 * Temprorary array with filtered items during filtering execution and after to be passed to filteredItems
 * @type {?}
 */
CfAutocompleteComponent.prototype.tempFiltered;
/**
 * @hidden 
 * Array with filtered items which is using inside dropdown in html
 * @type {?}
 */
CfAutocompleteComponent.prototype.filteredItems;
/**
 * @hidden 
 * Means if user focused search input field or is working with opened dropdown list items
 * @type {?}
 */
CfAutocompleteComponent.prototype.inputIsFocused;
/**
 * @hidden 
 * This is state of autocomplete to check from where search value was changed
 * @type {?}
 */
CfAutocompleteComponent.prototype.selectedByUser;
/**
 * @hidden 
 * This is autocomplete search value
 * @type {?}
 */
CfAutocompleteComponent.prototype.searchValue;
/**
 * @hidden 
 * It is array with selected items as chips
 * @type {?}
 */
CfAutocompleteComponent.prototype.selectedItems;
/**
 * @hidden 
 * It is array with selected items as multiselection by comma inside search input
 * @type {?}
 */
CfAutocompleteComponent.prototype.selection;
/**
 * @hidden 
 * Means selected items as labels inside search input separated by comma
 * @type {?}
 */
CfAutocompleteComponent.prototype.multipleSelected;
/**
 * @hidden 
 * If search value was changed by user
 * @type {?}
 */
CfAutocompleteComponent.prototype.typedByUser;
/**
 * @hidden 
 * Cache for search results
 * @type {?}
 */
CfAutocompleteComponent.prototype.cached;
/**
 * @hidden 
 * Timeout for user typing
 * @type {?}
 */
CfAutocompleteComponent.prototype.typingTimer;
/**
 * @hidden 
 * Object to register element state and compute differences.
 * @type {?}
 */
CfAutocompleteComponent.prototype.differ;
/** @type {?} */
CfAutocompleteComponent.prototype.elementRef;
/** @type {?} */
CfAutocompleteComponent.prototype.autocompleteService;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
